import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import type { SxProps, Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { IndentedSections } from '@openx/components-core/lib/IndentedSection/IndentedSections.tsx';
import { useTranslation } from 'react-i18next';
import type { ListRegionsQuery } from '../../../../../../graphql/fragment/audiences/ListRegions.generated.tsx';
import { MatchingOperator } from '../../../constants.ts';
import type { SubGroup } from '../../../types.ts';
import type { MarketOperatorRestrictionReason } from '../../../utils/marketOperatorRestrictions.ts';
import { OperatorChip, OperatorType } from '../../DimensionChip/OperatorChip.tsx';
import { OperatorSelector } from './OperatorSelector.tsx';
import { Segments } from './Segments.tsx';

type SubgroupMarketOperatorConfig = {
  isEdit: boolean;
  shouldUseRegionFirstFlow: boolean;
  subgroupRestrictionReasonByParentIndex: Record<number, MarketOperatorRestrictionReason | null>;
};

type SubgroupSummaryProps = {
  parentIndex: number;
  subgroup: SubGroup;
  listType: 'includes' | 'excludes';
  sx?: SxProps<Theme>;
  regions?: ListRegionsQuery['region'];
  marketOperatorConfig?: SubgroupMarketOperatorConfig;
};

export const SubgroupSummary = ({
  subgroup,
  parentIndex,
  sx,
  listType,
  regions = [],
  marketOperatorConfig,
}: SubgroupSummaryProps) => {
  const { t } = useTranslation();

  const {
    isEdit = true,
    shouldUseRegionFirstFlow = false,
    subgroupRestrictionReasonByParentIndex = {},
  } = marketOperatorConfig ?? {};

  const operatorType = subgroup.operator === MatchingOperator.INTERSECTION ? OperatorType.AND : OperatorType.OR;

  const parentSubgroupMarketReason = subgroupRestrictionReasonByParentIndex[parentIndex] ?? null;

  return (
    <Box sx={sx}>
      {subgroup.groups.map((group, subgroupIndex) => {
        const isLastItem = subgroupIndex + 1 === subgroup.groups.length;
        const subgroupName = t('Subgroup {childCount}', {
          childCount: subgroupIndex + 1,
        });

        return (
          <Stack key={subgroupIndex} spacing={0} sx={{ width: '100%' }}>
            {subgroupIndex === 0 && subgroup.groups.length > 1 && (
              <Box sx={{ width: '100%' }}>
                <OperatorSelector
                  isSubgroup
                  path={`normalized_definition.${listType}.groups.${parentIndex}.operator`}
                  readOnly={!isEdit}
                  andOperatorDisabled={isEdit && shouldUseRegionFirstFlow && parentSubgroupMarketReason != null}
                />
              </Box>
            )}
            <Typography
              variant="body1Bold"
              component="span"
              sx={{
                mb: 1,
              }}
            >
              {subgroupName}
            </Typography>
            {isLastItem ? (
              <Segments group={group} sx={{ pl: 3.5 }} listType={listType} regions={regions} />
            ) : (
              <>
                <IndentedSections sx={{ pl: 3.5, pb: 1 }}>
                  <Segments group={group} listType={listType} regions={regions} />
                </IndentedSections>

                <Box
                  sx={{
                    ml: -1.5,
                  }}
                >
                  <OperatorChip operator={operatorType} readonly hideDivider />
                </Box>

                <IndentedSections sx={{ pl: 3.5 }}>
                  <Box sx={{ height: '12px' }} />
                </IndentedSections>
              </>
            )}
          </Stack>
        );
      })}
    </Box>
  );
};
